import { Box, TextField, Typography, Divider } from '@mui/material';
import type { TeacherRules } from '../RulesConfigurationDialog';

export interface TeacherRulesTabProps {
  rules: TeacherRules;
  onChange: (rules: TeacherRules) => void;
}

export function TeacherRulesTab({ rules, onChange }: TeacherRulesTabProps) {
  const handleChange = (field: keyof TeacherRules, value: number) => {
    onChange({ ...rules, [field]: value });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Workload Limits */}
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Workload Limits
        </Typography>
        <Typography variant="caption" color="text.secondary" paragraph>
          Set maximum teaching periods per teacher to prevent overload. Used by auto-fill and teacher suggestions.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            label="Max Periods Per Day"
            type="number"
            value={rules.maxPeriodsPerDay}
            onChange={(e) => handleChange('maxPeriodsPerDay', parseInt(e.target.value) || 0)}
            inputProps={{ min: 1, max: 12 }}
            fullWidth
            helperText="Auto-fill will not assign more than this many periods to a teacher in one day"
          />
          <TextField
            label="Max Periods Per Week"
            type="number"
            value={rules.maxPeriodsPerWeek}
            onChange={(e) => handleChange('maxPeriodsPerWeek', parseInt(e.target.value) || 0)}
            inputProps={{ min: 1, max: 60 }}
            fullWidth
            helperText="Used to score workload balance — teachers closer to this limit rank lower"
          />
        </Box>
      </Box>

      <Divider />

      {/* Consecutive Periods */}
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Consecutive Periods
        </Typography>
        <Typography variant="caption" color="text.secondary" paragraph>
          Limit back-to-back teaching periods. Auto-fill skips teachers who would exceed this limit.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Max Consecutive Periods"
            type="number"
            value={rules.maxConsecutivePeriods}
            onChange={(e) => handleChange('maxConsecutivePeriods', parseInt(e.target.value) || 0)}
            inputProps={{ min: 1, max: 8 }}
            sx={{ width: '50%', pr: 1 }}
            helperText="Teachers with this many consecutive periods already assigned will be skipped by auto-fill"
          />
        </Box>
      </Box>

      <Divider />

      <Box
        sx={{
          p: 2,
          backgroundColor: 'info.light',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'info.main',
        }}
      >
        <Typography variant="caption" color="info.dark">
          <strong>Note:</strong> Max Periods Per Day and Max Consecutive Periods act as hard filters
          in auto-fill — teachers exceeding these limits are excluded from candidates.
          Max Periods Per Week is used as a soft score — teachers closer to the limit rank lower
          but are not excluded.
        </Typography>
      </Box>
    </Box>
  );
}
